import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Alert,
  Chip,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  Tab,
  Tabs,
  CircularProgress,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import axios from '../utils/axiosConfig';
import { handleApiError } from '../utils/apiErrorHandler';
import { CompetitionInfoContext } from './RegisteredCompetitionPage';
import { useAuth } from '../context/AuthContext';
import TrampolineScorecardDialog from '../components/TrampolineScorecardDialog';
import competitionCardsService, { CompetitionCard } from '../services/competitionCardsService';
import athleteExercisesService from '../services/athleteExercisesService';
import deregistrationService from '../services/deregistrationService';
import CardTokensManager from '../components/CardTokensManager';

import logger from '../utils/logger';

import { getAllowedExercisesByClassName, DEFAULT_EXERCISES, EXERCISE_RULES } from '../components/scorecarddata/exerciseRules';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Interface for exercise data
interface Exercise {
  id: string;
  name: string;
}

// Interface for competition data
interface Competition {
  _id: string;
  name: string;
  startDate: string;
  endDate: string;
  competitionGroups: CompetitionGroup[];
}

// Interface for competition group
interface CompetitionGroup {
  _id: string;
  name: string;
  type: 'Elite' | 'Basis';
}

// Interface for athlete data
interface Athlete {
  _id: string;
  firstname: string;
  surname: string;
  age: number;
  gender: string;
  competitionGroup: string;
  competitionGroupName: string;
  type: 'Elite' | 'Basis';
  deregistered?: boolean;
}


const calculateCardSubmissionDeadline = (startDate: string): string => {
  const startDateObj = new Date(startDate);
  const deadlineDate = new Date(startDateObj);
  deadlineDate.setDate(startDateObj.getDate() - 1);
  
  return deadlineDate.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const isDeadlinePassed = (deadlineDate: string): boolean => {
  if (!deadlineDate) return false;
  
  const parts = deadlineDate.split('.');
  const formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
  const deadline = new Date(formattedDate);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return today > deadline;
};

const CompetitionCardsPage: React.FC = () => {
  const { competitionId } = useParams<{ competitionId: string }>();
  const { clubId } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [cardSubmissionDeadline, setCardSubmissionDeadline] = useState<string>("");
  const [isDeadlineExpired, setIsDeadlineExpired] = useState<boolean>(false);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [eliteAthletes, setEliteAthletes] = useState<Athlete[]>([]);
  const [basisAthletes, setBasisAthletes] = useState<Athlete[]>([]);
  const [deregisteredAthletes, setDeregisteredAthletes] = useState<string[]>([]);
  const [competitionGroups, setCompetitionGroups] = useState<CompetitionGroup[]>([]);
  
  const [athleteExercises, setAthleteExercises] = useState<Record<string, string>>({});
  const [savingExercises, setSavingExercises] = useState<Record<string, boolean>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [loadingCards, setLoadingCards] = useState(false);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [loadingDeregistrations, setLoadingDeregistrations] = useState(false);

  const [competitionCards, setCompetitionCards] = useState<Record<string, CompetitionCard>>({});
  
  const [scorecardDialogOpen, setScorecardDialogOpen] = useState(false);
  const [currentAthlete, setCurrentAthlete] = useState<Athlete | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [currentCardTitle, setCurrentCardTitle] = useState<string | undefined>(undefined);
  // extraCard-Info pro Athleten (gemappt auf subdoc-ID via Name-Matching)
  const [extraCardInfo, setExtraCardInfo] = useState<Record<string, { extraCardCompetitionGroup: string }>>({});
  
  const [deregistrationDialogOpen, setDeregistrationDialogOpen] = useState(false);
  const [athleteToDeregister, setAthleteToDeregister] = useState<Athlete | null>(null);
  const [deregistrationReason, setDeregistrationReason] = useState('');
  const [isDeregistering, setIsDeregistering] = useState(false);

  const [unsavedExercises, setUnsavedExercises] = useState<Record<string, boolean>>({});
  const [savedExercises, setSavedExercises] = useState<Record<string, string>>({});
  
  const infoContext = useContext(CompetitionInfoContext);

  interface RegistrationAthlete {
    _id?: string;
    firstname: string;
    surname: string;
    age: number;
    gender: string;
    competitionGroup: string;
    competitionGroupName?: string;
  }

  // 🆕 Funktion zum Abrufen der erlaubten Übungen für einen Athleten
  const getAllowedExercisesForAthlete = (athlete: Athlete): Exercise[] => {
    if (!athlete || athlete.type !== 'Basis') {
      return [];
    }
    
    // Verwende eine der beiden Funktionen:
    return getAllowedExercisesByClassName(athlete.competitionGroupName);
    // oder:
    // return getAllowedExercisesByPattern(athlete.competitionGroupName);
  };

  // Load deregistered athletes
  const fetchDeregisteredAthletes = async () => {
    if (!competitionId || !clubId) return;
    
    try {
      setLoadingDeregistrations(true);
      
      const deregisteredAthletesData = await deregistrationService.getDeregisteredAthletes(competitionId, clubId);
      
      const deregisteredIds = deregisteredAthletesData.map(athlete => 
        athlete.originalAthleteId || ''
      ).filter(id => id);
      
      setDeregisteredAthletes(deregisteredIds);
      setLoadingDeregistrations(false);
    } catch (error) {
      logger.error('Error loading deregistered athletes', {error: error});
      setLoadingDeregistrations(false);
    }
  };

  const fetchCompetitionCards = async () => {
    if (!competitionId || !clubId) return;
    
    try {
      setLoadingCards(true);
      
      const cards = await competitionCardsService.getAllCompetitionCards(competitionId, clubId);
      
      const cardsMap: Record<string, CompetitionCard> = {};
      cards.forEach(card => {
        cardsMap[card.athlete] = card;
      });
      
      setCompetitionCards(cardsMap);
      setLoadingCards(false);
    } catch (error) {
      logger.error('Fehler beim Laden der Wettkampfkarten', {error: error});
      setLoadingCards(false);
    }
  };

  const fetchAthleteExercises = async () => {
    if (!competitionId || !clubId) return;
    
    try {
      setLoadingExercises(true);
      
      const exercisesMap = await athleteExercisesService.getAllExercises(competitionId, clubId);
      
      setAthleteExercises(exercisesMap);
      setSavedExercises(exercisesMap);
      setUnsavedExercises({});
      
      setLoadingExercises(false);
    } catch (error) {
      logger.error('Fehler beim Laden der Athleten-Übungen', {error: error});
      setLoadingExercises(false);
    }
  };

  const getUnsavedChangesCount = (): number => {
    return Object.keys(unsavedExercises).filter(
      athleteId => unsavedExercises[athleteId] && !isAthleteDeregistered(athleteId)
    ).length;
  };

  useEffect(() => {
    const fetchRegisteredAthletes = async () => {
      try {
        setLoading(true);
        
        const [competitionResponse, registrationResponse] = await Promise.all([
          axios.get(`/api/competitions/${competitionId}`),
          axios.get(`/api/competition-registrations/${competitionId}/${clubId}/latest`)
        ]);
        
        const competition = competitionResponse.data;
        const registration = registrationResponse.data;
        
        setCompetition(competition);
        setCompetitionGroups(competition.competitionGroups);
        
        if (competition.startDate) {
          const deadline = calculateCardSubmissionDeadline(competition.startDate);
          setCardSubmissionDeadline(deadline);
          
          const deadlinePassed = isDeadlinePassed(deadline);
          setIsDeadlineExpired(deadlinePassed);
        }

        if (!registration) {
          setAthletes([]);
          setEliteAthletes([]);
          setBasisAthletes([]);
          setLoading(false);
          return;
        }
        
        const registeredAthletes: Athlete[] = registration.athletes.map((athlete: RegistrationAthlete) => {
          return {
            _id: athlete._id || `athlete-${Math.random().toString(36).substr(2, 9)}`,
            firstname: athlete.firstname,
            surname: athlete.surname,
            age: athlete.age,
            gender: athlete.gender,
            competitionGroup: athlete.competitionGroup,
            competitionGroupName: athlete.competitionGroupName || athlete.competitionGroup,
            type: 'Basis',
            deregistered: false
          };
        });
        
        const eliteAthletes = registeredAthletes.filter(
          (athlete) =>
            athlete.competitionGroup &&
            competition.competitionGroups.some(
              (g: CompetitionGroup) => g._id === athlete.competitionGroup && g.type === "Elite"
            )
        );
        
        const basisAthletes = registeredAthletes.filter(
          (athlete) =>
            athlete.competitionGroup &&
            competition.competitionGroups.some(
              (g: CompetitionGroup) => g._id === athlete.competitionGroup && g.type === "Basis"
            )
        );
        
        eliteAthletes.forEach(athlete => { athlete.type = 'Elite'; });
        basisAthletes.forEach(athlete => { athlete.type = 'Basis'; });
        
        const exercises: Record<string, string> = {};
        basisAthletes.forEach(athlete => {
          exercises[athlete._id] = "";
        });
        
        setAthletes(registeredAthletes);
        setEliteAthletes(eliteAthletes);
        setBasisAthletes(basisAthletes);
        setAthleteExercises(exercises);

        setLoading(false);

        // Registrierungen laden um extraCard-Status per Sportler zu ermitteln
        const fetchExtraCardInfo = async () => {
          try {
            const regRes = await axios.get(`/api/registrations/${competitionId}/${clubId}`);
            const regs: Array<{ athlete: { firstname: string; surname: string }; extraCard?: boolean; extraCardCompetitionGroup?: string }> = regRes.data;
            const info: Record<string, { extraCardCompetitionGroup: string }> = {};
            registeredAthletes.forEach(a => {
              const reg = regs.find(
                r => r.athlete?.firstname?.toLowerCase() === a.firstname.toLowerCase() &&
                     r.athlete?.surname?.toLowerCase() === a.surname.toLowerCase() &&
                     r.extraCard
              );
              if (reg) {
                info[a._id] = { extraCardCompetitionGroup: reg.extraCardCompetitionGroup || '' };
              }
            });
            setExtraCardInfo(info);
          } catch { /* kein Fehler wenn nicht verfügbar */ }
        };

        await Promise.all([
          fetchCompetitionCards(),
          fetchAthleteExercises(),
          fetchDeregisteredAthletes(),
          fetchExtraCardInfo()
        ]);
      } catch (error) {
        handleApiError(error, 'Fehler beim Laden der registrierten Athleten.');
        setLoading(false);
      }
    };

    if (competitionId && clubId) {
      fetchRegisteredAthletes();
    }
  }, [competitionId, clubId]);

  useEffect(() => {
    if (deregisteredAthletes.length > 0) {
      const updatedEliteAthletes = eliteAthletes.map(athlete => ({
        ...athlete,
        deregistered: deregisteredAthletes.includes(athlete._id)
      }));
      
      const updatedBasisAthletes = basisAthletes.map(athlete => ({
        ...athlete,
        deregistered: deregisteredAthletes.includes(athlete._id)
      }));
      
      setEliteAthletes(updatedEliteAthletes);
      setBasisAthletes(updatedBasisAthletes);
    }
  }, [deregisteredAthletes]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleExerciseChange = (athleteId: string, event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setAthleteExercises(prev => ({
      ...prev,
      [athleteId]: value
    }));
    
    setUnsavedExercises(prev => ({
      ...prev,
      [athleteId]: true
    }));
  };

  const handleSaveExercises = async (athleteId: string) => {
    if (!competitionId || !clubId) return;
    
    try {
      setSavingExercises(prev => ({
        ...prev,
        [athleteId]: true
      }));
      
      await athleteExercisesService.saveExerciseForAthlete(
        competitionId,
        clubId,
        athleteId,
        athleteExercises[athleteId] || ""
      );
      
      setSavedExercises(prev => ({
        ...prev,
        [athleteId]: athleteExercises[athleteId] || ""
      }));
      
      setUnsavedExercises(prev => {
        const newUnsaved = { ...prev };
        delete newUnsaved[athleteId];
        return newUnsaved;
      });
      
      setSnackbarMessage('Übung erfolgreich gespeichert');
      setSnackbarOpen(true);
    } catch (error) {
      handleApiError(error, 'Fehler beim Speichern der Übung');
      setSnackbarMessage('Fehler beim Speichern der Übung');
      setSnackbarOpen(true);
    } finally {
      setSavingExercises(prev => ({
        ...prev,
        [athleteId]: false
      }));
    }
  };

  const handleSaveAllExercises = async () => {
    if (!competitionId || !clubId) return;
    
    const athletesToSave = Object.keys(unsavedExercises).filter(
      athleteId => unsavedExercises[athleteId] && !isAthleteDeregistered(athleteId)
    );
    
    if (athletesToSave.length === 0) return;
    
    try {
      const savingState: Record<string, boolean> = {};
      athletesToSave.forEach(athleteId => {
        savingState[athleteId] = true;
      });
      setSavingExercises(prev => ({ ...prev, ...savingState }));
      
      const savePromises = athletesToSave.map(athleteId =>
        athleteExercisesService.saveExerciseForAthlete(
          competitionId,
          clubId,
          athleteId,
          athleteExercises[athleteId] || ""
        )
      );
      
      await Promise.all(savePromises);
      
      const newSavedExercises: Record<string, string> = {};
      athletesToSave.forEach(athleteId => {
        newSavedExercises[athleteId] = athleteExercises[athleteId] || "";
      });
      
      setSavedExercises(prev => ({ ...prev, ...newSavedExercises }));
      setUnsavedExercises({});
      
      setSnackbarMessage(`${athletesToSave.length} Übung(en) erfolgreich gespeichert`);
      setSnackbarOpen(true);
    } catch (error) {
      handleApiError(error, 'Fehler beim Speichern der Übungen');
      setSnackbarMessage('Fehler beim Speichern der Übungen');
      setSnackbarOpen(true);
    } finally {
      const notSavingState: Record<string, boolean> = {};
      athletesToSave.forEach(athleteId => {
        notSavingState[athleteId] = false;
      });
      setSavingExercises(prev => ({ ...prev, ...notSavingState }));
    }
  };

  const handleShowDeregistrationDialog = (athlete: Athlete) => {
    setAthleteToDeregister(athlete);
    setDeregistrationReason('');
    setDeregistrationDialogOpen(true);
  };

  const handleDeregisterAthlete = async () => {
    if (!competitionId || !clubId || !athleteToDeregister) return;
    
    try {
      setIsDeregistering(true);
      
      await deregistrationService.deregisterAthlete(
        competitionId,
        clubId,
        athleteToDeregister._id,
        deregistrationReason
      );
      
      setDeregistrationDialogOpen(false);
      
      const updatedEliteAthletes = eliteAthletes.map(athlete => 
        athlete._id === athleteToDeregister._id 
          ? { ...athlete, deregistered: true } 
          : athlete
      );
      
      const updatedBasisAthletes = basisAthletes.map(athlete => 
        athlete._id === athleteToDeregister._id 
          ? { ...athlete, deregistered: true } 
          : athlete
      );
      
      setEliteAthletes(updatedEliteAthletes);
      setBasisAthletes(updatedBasisAthletes);
      
      setDeregisteredAthletes(prev => [...prev, athleteToDeregister._id]);
      
      setSnackbarMessage(`${athleteToDeregister.firstname} ${athleteToDeregister.surname} wurde erfolgreich abgemeldet`);
      setSnackbarOpen(true);
    } catch (error) {
      handleApiError(error, 'Fehler bei der Abmeldung des Athleten');
      setSnackbarMessage('Fehler bei der Abmeldung des Athleten');
      setSnackbarOpen(true);
    } finally {
      setIsDeregistering(false);
      setAthleteToDeregister(null);
    }
  };
  
  const handleCloseDeregistrationDialog = () => {
    setDeregistrationDialogOpen(false);
    setAthleteToDeregister(null);
    setDeregistrationReason('');
  };

  const handleOpenScorecard = (athlete: Athlete, cardIndex = 0, cardTitle?: string) => {
    setCurrentAthlete(athlete);
    setCurrentCardIndex(cardIndex);
    setCurrentCardTitle(cardTitle);
    setScorecardDialogOpen(true);
  };

  const handleViewScorecard = (athlete: Athlete, cardIndex = 0, cardTitle?: string) => {
    setCurrentAthlete(athlete);
    setCurrentCardIndex(cardIndex);
    setCurrentCardTitle(cardTitle);
    setScorecardDialogOpen(true);
  };

  const handleCloseScorecard = (hasData: boolean) => {
    fetchCompetitionCards();
    setScorecardDialogOpen(false);
    setCurrentAthlete(null);
    setCurrentCardIndex(0);
    setCurrentCardTitle(undefined);
  };

  const handleDeleteCard = async (athleteId: string) => {
    if (!competitionId || !clubId) return;
    
    try {
      if (!window.confirm('Möchtest Du diese Wettkampfkarte wirklich löschen?')) {
        return;
      }
      
      await competitionCardsService.deleteCompetitionCard(competitionId, clubId, athleteId);
      
      setCompetitionCards(prev => {
        const newCards = { ...prev };
        delete newCards[athleteId];
        return newCards;
      });
      
      setSnackbarMessage('Wettkampfkarte erfolgreich gelöscht');
      setSnackbarOpen(true);
    } catch (error) {
      handleApiError(error, 'Fehler beim Löschen der Wettkampfkarte.');
      setSnackbarMessage('Fehler beim Löschen der Wettkampfkarte');
      setSnackbarOpen(true);
    }
  };
  
  const isAthleteDeregistered = (athleteId: string): boolean => {
    return deregisteredAthletes.includes(athleteId);
  };

  const handleDeleteExercises = async (athleteId: string) => {
    if (!competitionId || !clubId) return;
    
    try {
      if (!window.confirm('Möchtest Du die ausgewählte Übung wirklich löschen?')) {
        return;
      }
      
      setSavingExercises(prev => ({
        ...prev,
        [athleteId]: true
      }));
      
      await athleteExercisesService.deleteExerciseForAthlete(competitionId, clubId, athleteId);
      
      setAthleteExercises(prev => ({
        ...prev,
        [athleteId]: ""
      }));
      
      setSavedExercises(prev => {
        const newSaved = { ...prev };
        delete newSaved[athleteId];
        return newSaved;
      });
      
      setUnsavedExercises(prev => {
        const newUnsaved = { ...prev };
        delete newUnsaved[athleteId];
        return newUnsaved;
      });
      
      setSnackbarMessage('Übung erfolgreich gelöscht');
      setSnackbarOpen(true);
    } catch (error) {
      handleApiError(error, 'Fehler beim Löschen der Übung');
      setSnackbarMessage('Fehler beim Löschen der Übung');
      setSnackbarOpen(true);
    } finally {
      setSavingExercises(prev => ({
        ...prev,
        [athleteId]: false
      }));
    }
  };

  const hasCompetitionCard = (athleteId: string): boolean => {
    return (
      competitionCards !== null && 
      typeof competitionCards === 'object' && 
      Object.keys(competitionCards).includes(athleteId)
    );
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const CardStatusChip: React.FC<{ 
    competitionId: string, 
    clubId: string, 
    athleteId: string 
  }> = ({ competitionId, clubId, athleteId }) => {
    const [cardStatus, setCardStatus] = useState<React.ReactElement>(
      <Chip 
        icon={<CircularProgress size={16} />} 
        label="Lädt..." 
        size="small" 
        color="default" 
      />
    );
  
    useEffect(() => {
      const fetchCardStatus = async () => {
        try {
          const response = await axios.get(`/api/competition-cards/${competitionId}/${clubId}/athlete/${athleteId}/exists`);
          
          let statusChip: React.ReactElement;
  
          if (!response.data.exists) {
            statusChip = (
              <Chip 
                icon={<CancelIcon />} 
                label="Nicht ausgefüllt" 
                size="small" 
                color="error" 
              />
            );
          } else {
            const card = response.data.card;
            
            if (!card.validationStatus?.isComplete) {
              statusChip = (
                <Chip 
                  icon={<WarningIcon />} 
                  label="Unvollständig" 
                  size="small" 
                  color="warning" 
                />
              );
            } else {
              statusChip = (
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label="Vollständig" 
                  size="small" 
                  color="success" 
                />
              );
            }
          }
  
          setCardStatus(statusChip);
        } catch (error) {
          logger.error('Fehler beim Abrufen des Kartenstatus', {error: error});
          setCardStatus(
            <Chip 
              icon={<ErrorIcon />} 
              label="Fehler" 
              size="small" 
              color="error" 
            />
          );
        }
      };
  
      fetchCardStatus();
    }, [athleteId, competitionId, clubId]);
  
    return cardStatus;
  };
  
  const ExerciseStatusChip: React.FC<{ 
    exercise: string;
    athleteId: string;
    unsaved: boolean;
    saved: string;
  }> = ({ exercise, athleteId, unsaved, saved }) => {
    if (!exercise) {
      return (
        <Chip 
          icon={<CancelIcon />} 
          label="Keine ausgewählt" 
          size="small" 
          color="error" 
        />
      );
    } else if (unsaved || exercise !== saved) {
      return (
        <Chip 
          icon={<WarningIcon />} 
          label="Nicht gespeichert" 
          size="small" 
          color="warning" 
        />
      );
    } else {
      return (
        <Chip 
          icon={<CheckCircleIcon />} 
          label="Gespeichert" 
          size="small" 
          color="success" 
        />
      );
    }
  };
  
  const renderCardStatus = (athleteId: string) => {
    if (!competitionId || !clubId) return null;
  
    return <CardStatusChip 
      competitionId={competitionId} 
      clubId={clubId} 
      athleteId={athleteId} 
    />;
  };

  return (
    <Container component="main" maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Wettkampfkarten & Übungen
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Für Elite-Meldungen bitte die Wettkampfkarten ausfüllen.<br />
          Für Basis-Meldungen bitte die erforderlichen Pflichtübungen auswählen.<br />
          Die Frist für die Abgabe der Wettkampfkarten ist der <strong>{cardSubmissionDeadline}</strong>.
          {isDeadlineExpired && (
            <Box sx={{ mt: 1, fontWeight: 'bold', color: 'error.main' }}>
              Die Abgabefrist ist bereits abgelaufen. Änderungen sind nicht mehr möglich.
            </Box>
          )}
        </Alert>
        
        {competitionId && clubId && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <CardTokensManager
              competitionId={competitionId}
              clubId={clubId}
            />
          </Box>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Elite" id="tab-0" aria-controls="tabpanel-0" />
            <Tab label="Basis" id="tab-1" aria-controls="tabpanel-1" />
          </Tabs>
        </Box>
        
        {loading || loadingDeregistrations ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Elite Tab */}
            <TabPanel value={tabValue} index={0}>
              {eliteAthletes.length === 0 ? (
                <Alert severity="info">Es wurden keine Elite-Athleten gefunden.</Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Athlet</TableCell>
                        <TableCell>Wettkampfklasse</TableCell>
                        <TableCell>Wettkampfkarte</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eliteAthletes.map((athlete) => (
                        <TableRow 
                          key={athlete._id} 
                          sx={{ 
                            backgroundColor: athlete.deregistered ? 'rgba(211, 47, 47, 0.08)' : 'inherit'
                          }}
                        >
                          <TableCell>
                            {athlete.firstname} {athlete.surname}
                            {athlete.deregistered && (
                              <Chip
                                label="Abgemeldet"
                                size="small"
                                color="warning"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{athlete.competitionGroupName}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => isDeadlineExpired
                                  ? handleViewScorecard(athlete)
                                  : handleOpenScorecard(athlete)}
                                disabled={athlete.deregistered}
                              >
                                {isDeadlineExpired
                                  ? 'Karte anschauen'
                                  : (competitionCards[athlete._id] ? 'Karte bearbeiten' : 'Karte ausfüllen')
                                }
                              </Button>
                              {extraCardInfo[athlete._id] && (
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  size="small"
                                  onClick={() => {
                                    const group = extraCardInfo[athlete._id].extraCardCompetitionGroup;
                                    isDeadlineExpired
                                      ? handleViewScorecard(athlete, 1, group)
                                      : handleOpenScorecard(athlete, 1, group);
                                  }}
                                  disabled={athlete.deregistered}
                                >
                                  {isDeadlineExpired ? 'Karte 2 anschauen' : 'Karte 2 (Pflicht) bearbeiten'}
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {athlete.deregistered ? (
                              <Chip 
                                icon={<CancelIcon />} 
                                label="Abgemeldet" 
                                size="small" 
                                color="warning" 
                              />
                            ) : (
                              renderCardStatus(athlete._id)
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {!athlete.deregistered && competitionCards[athlete._id] && !isDeadlineExpired && (
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteCard(athlete._id)}
                                  size="small"
                                  title="Karte löschen"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                              {!athlete.deregistered && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleShowDeregistrationDialog(athlete)}
                                >
                                  Sportler abmelden
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>
            
            {/* Basis Tab - 🆕 Mit gefilterten Übungen */}
            <TabPanel value={tabValue} index={1}>
              {basisAthletes.length === 0 ? (
                <Alert severity="info">Es wurden keine Basis-Athleten gefunden.</Alert>
              ) : (
                <>
                  {getUnsavedChangesCount() > 0 && !isDeadlineExpired && (
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveAllExercises}
                        disabled={Object.values(savingExercises).some(saving => saving)}
                        startIcon={<SaveIcon />}
                      >
                        Alle Änderungen speichern ({getUnsavedChangesCount()})
                      </Button>
                    </Box>
                  )}
                  
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Athlet</TableCell>
                          <TableCell>Wettkampfklasse</TableCell>
                          <TableCell>Pflichtübungen</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Aktionen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {basisAthletes.map((athlete) => {
                          // 🆕 Erlaubte Übungen für diesen Athleten abrufen
                          const allowedExercises = getAllowedExercisesForAthlete(athlete);
                          
                          return (
                            <TableRow 
                              key={athlete._id}
                              sx={{ 
                                backgroundColor: athlete.deregistered ? 'rgba(211, 47, 47, 0.08)' : 'inherit'
                              }}
                            >
                              <TableCell>
                                {athlete.firstname} {athlete.surname}
                                {athlete.deregistered && (
                                  <Chip
                                    label="Abgemeldet"
                                    size="small"
                                    color="warning"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>{athlete.competitionGroupName}</TableCell>
                              <TableCell>
                                <FormControl sx={{ width: '100%', maxWidth: 400 }}>
                                  <Select
                                    value={athleteExercises[athlete._id] || ""}
                                    onChange={(e) => handleExerciseChange(athlete._id, e)}
                                    disabled={athlete.deregistered || isDeadlineExpired}
                                    displayEmpty
                                    renderValue={(selected) => {
                                      if (!selected) {
                                        return <em>Bitte wählen</em>;
                                      }
                                      const exercise = allowedExercises.find(ex => ex.id === selected);
                                      return (
                                        <Box sx={{ display: 'flex' }}>
                                          <Chip 
                                            label={exercise?.name || selected} 
                                            size="small" 
                                          />
                                        </Box>
                                      );
                                    }}
                                  >
                                    {/* 🆕 Nur erlaubte Übungen anzeigen */}
                                    {allowedExercises.length > 0 ? (
                                      allowedExercises.map((exercise) => (
                                        <MenuItem key={exercise.id} value={exercise.id}>
                                          {exercise.name}
                                        </MenuItem>
                                      ))
                                    ) : (
                                      <MenuItem disabled>
                                        <em>Keine Übungen für diese Wettkampfklasse definiert</em>
                                      </MenuItem>
                                    )}
                                  </Select>
                                </FormControl>
                                
                                {/* 🆕 Hinweis, wenn keine Übungen erlaubt sind */}
                                {allowedExercises.length === 0 && (
                                  <Alert severity="warning" sx={{ mt: 1 }}>
                                    Für diese Wettkampfklasse wurden noch keine erlaubten Übungen konfiguriert. 
                                    Bitte wende Dich an den Veranstalter.
                                  </Alert>
                                )}
                              </TableCell>
                              <TableCell>
                                {athlete.deregistered ? (
                                  <Chip 
                                    icon={<CancelIcon />} 
                                    label="Abgemeldet" 
                                    size="small" 
                                    color="warning" 
                                  />
                                ) : (
                                  <ExerciseStatusChip 
                                    exercise={athleteExercises[athlete._id] || ""}
                                    athleteId={athlete._id}
                                    unsaved={unsavedExercises[athlete._id] || false}
                                    saved={savedExercises[athlete._id] || ""}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {!athlete.deregistered && !isDeadlineExpired && (
                                    <>
                                      <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={() => handleSaveExercises(athlete._id)}
                                        disabled={savingExercises[athlete._id] || allowedExercises.length === 0}
                                        startIcon={<SaveIcon />}
                                      >
                                        {savingExercises[athlete._id] ? 'Speichern...' : 'Speichern'}
                                      </Button>
                                      {athleteExercises[athlete._id] && (
                                        <IconButton
                                          color="error"
                                          onClick={() => handleDeleteExercises(athlete._id)}
                                          size="small"
                                          title="Übung löschen"
                                          disabled={savingExercises[athlete._id]}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      )}
                                    </>
                                  )}  
                                  {!athlete.deregistered && (
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      onClick={() => handleShowDeregistrationDialog(athlete)}
                                    >
                                      Sportler abmelden
                                    </Button>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </TabPanel>
          </>
        )}
        
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          message={snackbarMessage}
        />
      </Paper>

      {currentAthlete && competitionId && clubId && (
        <TrampolineScorecardDialog
          open={scorecardDialogOpen}
          competitionId={competitionId}
          clubId={clubId}
          athleteId={currentAthlete._id}
          athleteName={`${currentAthlete.firstname} ${currentAthlete.surname}`}
          competitionGroup={currentCardTitle || currentAthlete.competitionGroupName}
          onClose={handleCloseScorecard}
          readOnly={isDeadlineExpired}
          cardIndex={currentCardIndex}
          cardTitle={currentCardTitle}
        />
      )}
      
      <Dialog
        open={deregistrationDialogOpen}
        onClose={handleCloseDeregistrationDialog}
        aria-labelledby="deregistration-dialog-title"
      >
        <DialogTitle id="deregistration-dialog-title">
          Sportler abmelden
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest Du {athleteToDeregister?.firstname} {athleteToDeregister?.surname} wirklich vom Wettkampf abmelden?
            <br />
            Diese Aktion kann nicht rückgängig gemacht werden.<br />
            Hinweis: nach Meldeschluss abgemeldete Sportler haben keine Auswirkung auf die Meldegebühr.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label="Grund für die Abmeldung (optional)"
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={deregistrationReason}
            onChange={(e) => setDeregistrationReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeregistrationDialog} color="primary">
            Abbrechen
          </Button>
          <Button 
            onClick={handleDeregisterAthlete} 
            color="error" 
            variant="contained"
            disabled={isDeregistering}
          >
            {isDeregistering ? 'Wird abgemeldet...' : 'Abmelden'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompetitionCardsPage;